import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Stack, TextField, Chip, IconButton, Collapse,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { format } from 'date-fns';
import { liveKitService } from '../services/api';
import SessionsDrillDown from './SessionsDrillDown';
import { usePagination } from '../hooks/usePagination';

const today = () => new Date().toISOString().split('T')[0];
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
};

// Employee's own "when did I join/leave, how long did I stay" record — the self-service
// counterpart to LiveKitAdmin's per-project report. Date-wise, across every project the employee
// has ever connected to (not just their currently-assigned ones — history persists even if the
// assignment is later removed), defaulting to the last 30 days. Each row expands to show the
// individual sessions (drill-down) that make up its summed totals.
const MyMeetingHistory: React.FC = () => {
  const [fromDate, setFromDate] = useState(daysAgo(30));
  const [toDate, setToDate] = useState(today());
  const [rows, setRows] = useState<any[] | null>(null);

  // presenceDayId of the currently-expanded row (only one open at a time), and a cache of
  // fetched sessions per presenceDayId so re-expanding a row already opened this page load
  // doesn't refetch.
  const [expandedDayId, setExpandedDayId] = useState<number | null>(null);
  const [sessionsByDay, setSessionsByDay] = useState<Record<number, any[]>>({});

  const load = useCallback(async () => {
    try {
      const data = await liveKitService.getMyHistory(fromDate, toDate);
      setRows(data);
    } catch {
      // LiveKit may be disabled/unavailable — don't block the attendance page on it.
      setRows(null);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    load();
  }, [load]);

  // Called unconditionally (rules of hooks) even while rows is still null/loading — `rows ?? []`
  // just pages an empty list until the first successful fetch lands.
  const { pagedRows, paginationProps } = usePagination(rows ?? []);

  const toggleExpand = async (presenceDayId: number) => {
    if (expandedDayId === presenceDayId) {
      setExpandedDayId(null);
      return;
    }
    setExpandedDayId(presenceDayId);
    if (!sessionsByDay[presenceDayId]) {
      try {
        const sessions = await liveKitService.getMySessions(presenceDayId);
        setSessionsByDay(prev => ({ ...prev, [presenceDayId]: sessions || [] }));
      } catch {
        setSessionsByDay(prev => ({ ...prev, [presenceDayId]: [] }));
      }
    }
  };

  // Nothing rendered when the feature is unreachable, matching the other LiveKit widgets on this
  // page — but an empty (successfully fetched) history still renders the table with an empty state,
  // since "you have no meeting history yet" is useful information, unlike "feature not available".
  if (rows === null) return null;

  const totalDurationSeconds = rows.reduce((sum, r) => sum + (r.totalDurationSeconds || 0), 0);

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
        <Typography variant="subtitle1" fontWeight={600}>My Meeting History</Typography>
        <Stack direction="row" spacing={1}>
          <TextField
            size="small" label="From" type="date" value={fromDate}
            onChange={e => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: toDate }}
          />
          <TextField
            size="small" label="To" type="date" value={toDate}
            onChange={e => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: today(), min: fromDate }}
          />
        </Stack>
      </Stack>

      {rows.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Total time connected in this range: {Math.round(totalDurationSeconds / 60)} min. Click a row to see individual sessions.
        </Typography>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }} />
              {['Date', 'Project', 'First Join', 'Last Leave', 'Sessions', 'Total Time', 'Status'].map(h => (
                <TableCell key={h} sx={{ fontWeight: 'bold' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>No meeting activity in this date range.</TableCell></TableRow>
            ) : pagedRows.map((row, idx) => {
              const expanded = expandedDayId === row.presenceDayId;
              return (
                <React.Fragment key={idx}>
                  <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggleExpand(row.presenceDayId)}>
                    <TableCell>
                      <IconButton size="small">
                        {expanded ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{format(new Date(row.date), 'dd MMM yyyy')}</TableCell>
                    <TableCell>{row.projectName}</TableCell>
                    <TableCell>{row.firstJoin ? format(new Date(row.firstJoin), 'HH:mm') : '—'}</TableCell>
                    <TableCell>{row.lastLeave ? format(new Date(row.lastLeave), 'HH:mm') : '—'}</TableCell>
                    <TableCell>{row.sessionCount}</TableCell>
                    <TableCell>{Math.round((row.totalDurationSeconds || 0) / 60)} min</TableCell>
                    <TableCell>
                      {row.connected ? <Chip label="Connected" color="success" size="small" /> : <Box component="span" sx={{ color: 'text.secondary' }}>Ended</Box>}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }}>
                      <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <Box sx={{ bgcolor: 'action.hover' }}>
                          <SessionsDrillDown sessions={expanded ? (sessionsByDay[row.presenceDayId] ?? null) : null} />
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {rows.length > 0 && <TablePagination {...paginationProps} />}
    </Paper>
  );
};

export default MyMeetingHistory;
